import type { ReactNode } from 'react';
import React, { useMemo, useState } from 'react';
import type { PaletteMode, Theme } from '@mui/material';
import { ThemeProvider } from '@mui/material';
import {
  darkTheme as defaultDarkTheme,
  lightTheme as defaultLightTheme,
} from '../utils/theme';
import { ThemeContext } from './ThemeContext';

interface ThemeProviderProps {
  children: ReactNode;
  lightTheme?: Theme;
  darkTheme?: Theme;
}

// Immediately retrieve the saved theme mode to prevent flicker
const getInitialThemeMode = (): PaletteMode => {
  const savedMode = localStorage.getItem('themeMode') as PaletteMode;
  return savedMode || 'light'; // Default to 'light' if no saved mode
};

export const ThemeContextProvider: React.FC<ThemeProviderProps> = ({
  children,
  lightTheme = defaultLightTheme,
  darkTheme = defaultDarkTheme,
}) => {
  const [mode, setMode] = useState<PaletteMode>(getInitialThemeMode);

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode); // Save the user's theme preference
      return newMode;
    });
  };

  const theme = useMemo(
    () => (mode === 'light' ? lightTheme : darkTheme),
    [mode, lightTheme, darkTheme]
  );

  const value = useMemo(() => ({ mode, toggleTheme }), [mode, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
